import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Snowflake } from "lucide-react";
import { api, fmtNum, fmtRp, fmtDate, COMPANY, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1 border-b border-dashed border-slate-200 text-sm"><span className="text-slate-500">{label}</span><span className="font-semibold text-slate-900 text-right">{value}</span></div>
);

export default function NotaGroup() {
  const { groupNo } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/transactions/group/${groupNo}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, [groupNo]);

  if (err) return <div className="min-h-screen aqua-bg flex items-center justify-center text-rose-600" data-testid="nota-group-error">{err}</div>;
  if (!d) return <div className="min-h-screen aqua-bg flex items-center justify-center text-slate-500">Memuat nota gabungan...</div>;
  const isIn = d.type === "in";
  const priceLabel = isIn ? "Harga Beli" : "Harga Jual";

  return (
    <div className="min-h-screen aqua-bg py-8 px-4" data-testid="nota-group-page">
      <div className="max-w-[800px] mx-auto mb-4 flex items-center justify-between no-print">
        <Link to="/transactions" className="inline-flex items-center gap-1 text-sm text-sky-700 font-semibold hover:underline" data-testid="link-back-transactions"><ArrowLeft className="h-4 w-4" /> Kembali</Link>
        <Button onClick={() => window.print()} className="rounded-full bg-sky-600 hover:bg-sky-700" data-testid="btn-print-nota-group"><Printer className="h-4 w-4 mr-1" /> Cetak / Simpan PDF</Button>
      </div>

      <div className="nota-sheet max-w-[800px] mx-auto bg-white rounded-2xl border border-sky-100 shadow-xl p-8 text-slate-900" data-testid="nota-group-sheet">
        <div className="flex items-start justify-between gap-4 pb-5 border-b-2 border-slate-900">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14"><img src={COMPANY.logo} alt="Logo" className="h-full w-full object-contain" /></div>
            <div>
              <div className="font-extrabold tracking-tight text-lg uppercase leading-tight">{COMPANY.name}</div>
              <div className="text-xs text-slate-500">{COMPANY.tagline} • {COMPANY.address}</div>
            </div>
          </div>
          <div className="text-right">
            <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${isIn ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`} data-testid="nota-group-type">{isIn ? "Nota Penerimaan Gabungan" : "Nota Penjualan Gabungan"}</div>
            <div className="mt-2 font-mono text-sm font-bold" data-testid="nota-group-number">{d.group_no}</div>
            <div className="text-xs text-slate-500">{fmtDate(d.date)}</div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-x-8">
          {isIn ? <Row label="Asal Ikan" value={d.origin_name || "-"} /> : <Row label="Tujuan / Pembeli" value={d.destination || "-"} />}
          <Row label="Pembayaran" value={d.payment === "credit" ? (isIn ? "Kredit (Hutang)" : "Kredit (Piutang)") : "Tunai"} />
          <Row label="Operator" value={d.user_name} />
          <Row label="Jumlah Baris" value={`${d.items.length} batch`} />
        </div>
        {d.note && <div className="mt-2 text-sm"><span className="text-slate-500">Catatan: </span>{d.note}</div>}

        <table className="w-full text-sm mt-5" data-testid="nota-group-table">
          <thead><tr className="border-y border-slate-300 text-[11px] uppercase tracking-wider text-slate-500"><th className="text-left py-2 pr-2">#</th><th className="text-left py-2 pr-2">Kode Batch</th><th className="text-left py-2 pr-2">Jenis Ikan</th><th className="text-left py-2 pr-2">Size</th><th className="text-right py-2 pr-2">Jumlah</th><th className="text-right py-2 pr-2">{priceLabel}</th><th className="text-right py-2">Subtotal</th></tr></thead>
          <tbody>
            {d.items.map((it, i) => (
              <tr key={it.id} className="border-b border-dashed border-slate-200" data-testid={`nota-group-row-${i}`}>
                <td className="py-2 pr-2 text-slate-400">{i + 1}</td>
                <td className="py-2 pr-2 font-mono text-xs">{it.batch_code}<div className="text-[10px] text-slate-400">{it.receipt_no}</div></td>
                <td className="py-2 pr-2 font-semibold">{it.species_name}</td>
                <td className="py-2 pr-2">{it.size}</td>
                <td className="py-2 pr-2 text-right font-mono">{fmtNum(it.quantity_mc)} <span className="text-xs text-slate-500">{it.unit}</span></td>
                <td className="py-2 pr-2 text-right font-mono">{it.unit_price ? fmtRp(it.unit_price) : "—"}</td>
                <td className="py-2 text-right font-mono font-semibold">{it.subtotal != null ? fmtRp(it.subtotal) : ""}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-900 font-bold">
              <td colSpan={4} className="py-2.5 pr-2">TOTAL</td>
              <td className="py-2.5 pr-2 text-right font-mono" data-testid="nota-group-total-qty">{fmtNum(d.total_mc)} MC{d.total_kg_unit > 0 && <><br />{fmtNum(d.total_kg_unit)} kg</>}</td>
              <td className="py-2.5 pr-2 text-right text-xs text-slate-500 font-normal">±{fmtNum(d.total_weight_kg)} kg</td>
              <td className="py-2.5 text-right font-mono text-base" data-testid="nota-group-total-value">{fmtRp(d.total_value)}</td>
            </tr>
          </tfoot>
        </table>

        {!isIn && (
          <div className="mt-4 rounded-xl border border-slate-200 p-3 text-xs">
            <div className="font-bold uppercase tracking-wider text-slate-500 mb-1.5">Pembayaran ke rekening</div>
            {COMPANY.banks.map((b) => <div key={b.bank} className="flex justify-between"><span className="text-slate-600">Bank {b.bank}</span><span className="font-mono font-semibold">{b.account_no} <span className="font-sans font-normal text-slate-500">a.n. {b.holder}</span></span></div>)}
          </div>
        )}

        <div className="mt-8 grid grid-cols-3 gap-4 text-center text-xs">
          {[["Diserahkan oleh", d.user_name], [isIn ? "Sopir / Pengirim" : "Sopir / Pengangkut", ""], [isIn ? "Diterima Gudang" : "Diterima Pembeli", ""]].map(([t, n]) => (
            <div key={t}><div className="h-16 border-b border-slate-400" /><div className="mt-1.5 font-semibold text-slate-700">{t}</div><div className="text-slate-500 h-4">{n}</div></div>
          ))}
        </div>
        <div className="mt-6 pt-4 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-400">
          <span className="inline-flex items-center gap-1"><Snowflake className="h-3 w-3" /> Disimpan pada suhu -22°C</span>
          <span>Mencakup nota: {d.receipts.join(", ")} • Dicetak {new Date().toLocaleDateString("id-ID")}</span>
        </div>
      </div>
    </div>
  );
}
